import React from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router"

import { Page404 } from "@/components/nav/page404"
import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganizationId } from "@/contexts"
import { AdminEntityFieldsSkeleton } from "@/modules/admin/components/admin-entity-fields-skeleton"
import { useAdminMeterPageState } from "@/modules/admin/meters"
import { useSites } from "@/services/site/use-sites"
import { useUrl } from "@/utils/url"

import { Button, Link as MuiLink, Stack } from "@mui/material"

import { AdminMeterEditForm } from "./admin-meter-edit-form"

export const AdminMeterEditRoute: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const { activeOrganizationId } = useActiveOrganizationId()
  const { adminMeterPath, adminMetersPath } = useUrl()
  const {
    meterData,
    meterId,
    meterIsFetching,
    meterIsLoading,
    meterIsUnavailable,
  } = useAdminMeterPageState()

  const organizationId = activeOrganizationId ?? ""

  const { sitesData, sitesIsFetching } = useSites({
    apiQueryOptions: {
      where: { organization_id: organizationId },
    },
    queryOptions: {
      enabled: Boolean(organizationId),
    },
  })

  if (!organizationId) {
    return null
  }

  if (meterIsUnavailable) {
    return <Page404 />
  }

  if (meterIsLoading) {
    return (
      <Stack sx={{ gap: 2 }}>
        <PageTitle
          breadcrumbActions={
            <>
              <MuiLink
                component={Link}
                to={`${adminMetersPath}${location.search}`}
                underline="hover"
              >
                {t("admin.meters")}
              </MuiLink>
              {meterId !== undefined ? (
                <MuiLink
                  component={Link}
                  to={`${adminMeterPath(String(meterId))}${location.search}`}
                  underline="hover"
                >
                  {t("admin.meterDetail.viewMeter")}
                </MuiLink>
              ) : null}
            </>
          }
          heading={t("editMeter.title")}
          primaryActions={
            meterId === undefined ? undefined : (
              <Button
                onClick={() => {
                  void navigate(
                    `${adminMeterPath(String(meterId))}${location.search}`,
                    {
                      replace: true,
                    }
                  )
                }}
                variant="outlined"
              >
                {t("button.cancel")}
              </Button>
            )
          }
        />
        <PageCard>
          <AdminEntityFieldsSkeleton
            ariaLabel={t("loadingAriaLabel", {
              title: t("admin.meterDetail.title"),
            })}
            roundedInputs
          />
        </PageCard>
      </Stack>
    )
  }

  if (!meterData || meterId === undefined) {
    return null
  }

  return (
    <AdminMeterEditForm
      key={meterId}
      meter={meterData}
      meterId={meterId}
      meterIsFetching={meterIsFetching}
      sites={sitesData ?? []}
      sitesIsFetching={sitesIsFetching}
    />
  )
}
